import { Box } from '@mui/system'
import { Typography } from '@mui/material'
import { useMemo, type FC } from 'react'

interface CameraTimelineHeaderProps {
  cameraId: string
  cameraName: string
  cameraType: string
  rowIndex: number
  isSelected: boolean
  accessAllowed: boolean
  hasRecordings: boolean
  onCameraSelect: (cameraId: string) => void
}

const timelineHeaderStyles = {
  gridColumn: 1,
  display: 'flex',
  flexDirection: 'row',
  gap: 'var(--spacing-xs)',
  whiteSpace: 'nowrap',
  background: 'none',
  border: 0,
  padding: 0,
  font: 'inherit',
  color: 'inherit',
  textAlign: 'left',
  cursor: 'pointer'
}

export const CameraTimelineHeader: FC<CameraTimelineHeaderProps> = ({
  cameraId,
  cameraName,
  cameraType,
  rowIndex,
  isSelected,
  hasRecordings,
  accessAllowed,
  onCameraSelect
}) => {
  /**
   * There's only 1 reason a camera should NOT be selectable: it has accessAllowed=true but has
   * no recordings. In that case, the timeline header should be disabled and not clickable.
   * If a camera has accessAllowed=false and no recordings, it should still be selectable to
   * allow the reason to show in the error banner
   */
  const isSelectable = useMemo(() => {
    return accessAllowed ? hasRecordings : true
  }, [accessAllowed, hasRecordings])

  const dynamicHeaderStyles = useMemo(() => {
    if (!isSelectable) {
      return { ...timelineHeaderStyles, cursor: 'default', opacity: 0.5 }
    }
    return timelineHeaderStyles
  }, [isSelectable])

  return (
    <Box
      gridRow={rowIndex}
      sx={dynamicHeaderStyles}
      component={isSelectable ? 'button' : 'div'}
      type={isSelectable ? 'button' : undefined}
      aria-pressed={isSelectable ? isSelected : undefined}
      onClick={isSelectable ? () => onCameraSelect(cameraId) : undefined}
      data-testid={`camera-timeline-label-${cameraId}`}
    >
      <Typography variant="body2" fontSize="var(--size-m)" component="span">
        {rowIndex}.
      </Typography>
      <Box display="flex" flexWrap="wrap">
        <Typography component="span" variant="body2" fontSize="var(--size-m)" marginRight="var(--spacing-xs)">
          {cameraType}
        </Typography>
        <Typography component="span" variant="body2" fontSize="var(--size-m)" fontWeight="500" color={isSelected ? 'var(--yellow-600)' : undefined}>
          {cameraName}
        </Typography>
      </Box>
    </Box>
  )
}

CameraTimelineHeader.displayName = 'CameraTimelineHeader'
